import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

export interface ApiClientConfig {
  baseURL: string;
  tenantId?: string;
  siteId?: number;
  apiKey?: string;
  personRoleId?: number;
  onUnauthorized?: () => void;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

class ApiClient {
  private axiosInstance: AxiosInstance;
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add X-Tenant-ID header if tenantId is provided
        if (this.config.tenantId) {
          config.headers['X-Tenant-ID'] = this.config.tenantId;
        }

        // Add X-Site-ID header if siteId is provided (for explicit siteId passing)
        if (this.config.siteId) {
          config.headers['X-Site-ID'] = this.config.siteId.toString();
        }

        // The apiKey is actually a JWT issued by /api/Authentication/Login -- the
        // backend authenticates via JWT Bearer (see FitTrackApi Program.cs), which
        // reads the standard Authorization header, not X-API-Key. X-API-Key was the
        // pre-JWT auth mechanism; ApiKeyAuthenticationMiddleware is now a documented
        // no-op, so sending only X-API-Key meant every [Authorize]-protected endpoint
        // rejected the request before it reached controller code.
        if (this.config.apiKey) {
          config.headers['Authorization'] = `Bearer ${this.config.apiKey}`;
        }

        // Add PersonRoleID so the API can resolve the user's role for authorization
        if (this.config.personRoleId) {
          config.headers['X-PersonRoleID'] = this.config.personRoleId.toString();
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Handle 401 Unauthorized - but skip for auth endpoints to avoid logout loops
        if (error.response?.status === 401 && this.config.onUnauthorized) {
          const url = error.config?.url?.toLowerCase() || '';
          if (!url.includes('/authentication/') && !url.includes('/login') && !url.includes('/logout')) {
            this.config.onUnauthorized();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  public updateTenantId(tenantId: string): void {
    this.config.tenantId = tenantId;
  }

  public updateApiKey(apiKey: string): void {
    this.config.apiKey = apiKey;
  }

  public updatePersonRoleId(personRoleId: number): void {
    this.config.personRoleId = personRoleId;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.get(url, config);
      return this.unwrapResponse<T>(response.data);
    } catch (error: any) {
      return this.handleError<T>(error);
    }
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.post(url, data, config);
      return this.unwrapResponse<T>(response.data);
    } catch (error: any) {
      return this.handleError<T>(error);
    }
  }

  // Multipart file upload. The instance default 'Content-Type: application/json' header
  // must be removed (not just overridden to 'multipart/form-data') for this request --
  // axios's FormData handling only leaves the body untouched when it does NOT see a JSON
  // content type already set; otherwise it JSON-stringifies the FormData instead of
  // sending it as binary multipart. Explicitly unsetting the header (rather than naming
  // 'multipart/form-data' ourselves) also lets the browser attach the required boundary
  // parameter, which the browser only does when it is the one setting the header.
  public async postForm<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.post(url, formData, {
        ...config,
        headers: { ...config?.headers, 'Content-Type': undefined as unknown as string },
      });
      return this.unwrapResponse<T>(response.data);
    } catch (error: any) {
      return this.handleError<T>(error);
    }
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.put(url, data, config);
      return this.unwrapResponse<T>(response.data);
    } catch (error: any) {
      return this.handleError<T>(error);
    }
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse = await this.axiosInstance.delete(url, config);
      return this.unwrapResponse<T>(response.data);
    } catch (error: any) {
      return this.handleError<T>(error);
    }
  }

  // Unwrap the C# ApiResponse wrapper if present
  private unwrapResponse<T>(body: any): ApiResponse<T> {
    if (body && typeof body === 'object' && 'success' in body) {
      return {
        success: body.success,
        data: body.data as T,
        message: body.message || body.errorMessage,
        errors: body.errors,
      };
    }
    return {
      success: true,
      data: body as T,
    };
  }

  private handleError<T>(error: any): ApiResponse<T> {
    if (error.response) {
      // Server responded with error
      const errorMsg = error.response.data?.message || error.response.data?.errorMessage || error.message || 'An error occurred';
      const errorDetails = error.response.data?.errorDetails;
      return {
        success: false,
        message: errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg,
        errors: error.response.data?.errors || [],
      };
    } else if (error.request) {
      // Request was made but no response
      return {
        success: false,
        message: 'No response from server',
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
      };
    }
  }
}

// Factory function to create API client instances
export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}

export default ApiClient;
